import { call } from '@/lib/api';
import type { Document } from '@/types';
import { Group, Text, rem } from '@mantine/core';
import { Dropzone, FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FileError } from 'react-dropzone-esm';
import { PiUploadLight, PiXLight } from 'react-icons/pi';
import { VscFileMedia } from 'react-icons/vsc';

const MAX_FILE_SIZE = 20 * 1024 ** 2;
const ALLOWED_MIME_TYPES = [
  MIME_TYPES['7z'],
  MIME_TYPES.doc,
  MIME_TYPES.docx,
  MIME_TYPES.jpeg,
  MIME_TYPES.pdf,
  MIME_TYPES.xls,
  MIME_TYPES.xlsx,
  MIME_TYPES.zip,
];

const formatError = (file: File, error: FileError) => {
  if (error.code === 'file-invalid-type') {
    return {
      title: 'Invalid file type',
      message: `${file.name} is of unsupported file type. We can support only: 7z, doc, docx, jpeg, pdf, xls, xlsx, zip`,
    };
  } else if (error.code === 'file-too-large') {
    return {
      title: 'File too large',
      message: `${file.name} must be smaller than 20MB`,
    };
  } else {
    return {
      title: error.code,
      message: error.message,
    };
  }
};

const useUploadDocument = (pulseId: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      type,
      files,
    }: {
      type: number;
      files: FileWithPath[];
    }) => {
      const body = new FormData();
      body.append('pulse', String(pulseId));
      body.append('type', type.toString());
      files.forEach((f) => body.append(f.name, f));
      const res = await call<Document[]>(`document/upload`, { body });
      return res.json();
    },
    onSuccess: (documents) => {
      queryClient.setQueryData(
        ['documents', 'list', pulseId],
        (old: Document[] = []) => [...old, ...documents],
      );
    },
  });
};

export default function DocumentsUpload({
  type,
  pulseId,
  onUploadSuccess,
  maxDocuments,
}: {
  type: number;
  pulseId: string | number;
  onUploadSuccess?: (documents: Document[]) => void;
  maxDocuments?: number;
}) {
  const mutation = useUploadDocument(pulseId);
  const queryClient = useQueryClient();

  // Get current documents count
  const currentDocuments =
    queryClient.getQueryData<Document[]>(['documents', 'list', pulseId]) || [];
  const currentCount = currentDocuments.length;

  return (
    <>
      <Dropzone
        loading={mutation.isPending}
        onDrop={async (files) => {
          // Check if adding these files would exceed the limit
          if (maxDocuments && currentCount + files.length > maxDocuments) {
            const remainingSlots = Math.max(0, maxDocuments - currentCount);

            if (remainingSlots === 0) {
              notifications.show({
                title: 'Document limit reached',
                message: `Maximum of ${maxDocuments} documents allowed. Please remove some documents before uploading new ones.`,
                color: 'red',
                autoClose: 5000,
              });
              return;
            } else {
              notifications.show({
                title: 'Document limit exceeded',
                message: `Can only upload ${remainingSlots} more document${remainingSlots === 1 ? '' : 's'}. Only the first ${remainingSlots} will be uploaded.`,
                color: 'orange',
                autoClose: 5000,
              });
              // Only upload files that fit within the limit
              files = files.slice(0, remainingSlots);
            }
          }

          files.forEach((file) => {
            mutation.mutate(
              { type: type, files: [file] },
              {
                onSuccess: (documents) => {
                  if (onUploadSuccess) {
                    onUploadSuccess(documents);
                  }
                },
              },
            );
          });
        }}
        onReject={(files) => {
          files.forEach(({ file, errors }) => {
            errors.forEach((error) => {
              const { title, message } = formatError(file, error);
              notifications.show({
                title,
                message,
                color: 'red',
                autoClose: 5000,
              });
            });
          });
        }}
        maxSize={MAX_FILE_SIZE}
        accept={ALLOWED_MIME_TYPES}
      >
        <Group justify="center" gap="xl" style={{ pointerEvents: 'none' }}>
          <Dropzone.Accept>
            <PiUploadLight
              style={{
                width: rem(32),
                height: rem(32),
                color: 'var(--mantine-color-blue-6)',
              }}
            />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <PiXLight
              style={{
                width: rem(32),
                height: rem(32),
                color: 'var(--mantine-color-red-6)',
              }}
            />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <VscFileMedia
              style={{
                width: rem(32),
                height: rem(32),
                color: 'var(--mantine-color-dimmed)',
              }}
            />
          </Dropzone.Idle>

          <div>
            <Text inline>Drop files or click here to upload documents</Text>
          </div>
        </Group>
      </Dropzone>
    </>
  );
}
